import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { createNumberFormatFieldWithAdornment } from '@roc/ui';
import { ScopeOfWorkV2FormBaseStore } from '@roc/feature-sow-shared/stores/v2';
import { observer } from 'mobx-react';
import { Home, HomeWork } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(3),
  },

  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: '130px 1fr 1fr',
    rowGap: theme.spacing(1.5),
    columnGap: theme.spacing(1.5),
    alignItems: 'center',
    maxWidth: '58%',
  },

  gridWithoutPreRehab: {
    gridTemplateColumns: '130px 1fr',
    maxWidth: '40%',
  },

  label: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },

  preHeader: {
    height: 40,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    background: '#747474',
    color: '#FFFFFF',
    gap: theme.spacing(1),
  },

  postHeader: {
    height: 40,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    background: '#E6F1FB',
    color: '#185FA5',
    gap: theme.spacing(1),
  },

  field: {
    '& .MuiFormControl-root': {
      width: '100%',
    },
  },


  sameAsPreRehab: {
    height: 40,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.disabledBackground,
    fontSize: 14,
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
}));

type Props = {
  store: ScopeOfWorkV2FormBaseStore;
  disabled?: boolean;
};

const RehabDetailsSection = observer(({ store, disabled }: Props) => {
  const classes = useStyles();
  const showPreRehab = !store.isGroundUpLoan;

  const SameAsPreRehab = () => (
    <Box className={classes.sameAsPreRehab}>Same as pre-rehab</Box>
  );

  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        Property Details
      </Typography>

      <Box
        className={`${classes.grid} ${showPreRehab ? '' : classes.gridWithoutPreRehab}`}
      >
        {/* Headers */}
        <Box />
        {showPreRehab && (
          <Box className={classes.preHeader}><Home fontSize="small" />Pre-Rehab</Box>
        )}
        <Box className={classes.postHeader}><HomeWork fontSize="small" />Post-Rehab</Box>

        {/* Bedrooms */}
        <Typography className={classes.label}>Bedrooms</Typography>

        {showPreRehab && (
          <Box className={classes.field}>
            {createNumberFormatFieldWithAdornment({
              store,
              testId: 'preRehabBedroomsCount',
              fieldName: 'preRehabBedroomsCount',
              label: '',
              allowNegative: false,
              thousandSeparator: true,
              decimalScale: 1,
              disabled: disabled,
            })}
          </Box>
        )}

        <Box className={classes.field}>
          {createNumberFormatFieldWithAdornment({
            store,
            testId: 'postRehabBedroomsCount',
            fieldName: 'postRehabBedroomsCount',
            label: '',
            allowNegative: false,
            thousandSeparator: true,
            decimalScale: 1,
            disabled: disabled,
          })}
        </Box>

        {/* Bathrooms */}
        <Typography className={classes.label}>Bathrooms</Typography>

        {showPreRehab && (
          <Box className={classes.field}>
            {createNumberFormatFieldWithAdornment({
              store,
              testId: 'preRehabBathroomsCount',
              fieldName: 'preRehabBathroomsCount',
              label: '',
              allowNegative: false,
              thousandSeparator: true,
              decimalScale: 1,
              disabled: disabled,
            })}
          </Box>
        )}

        <Box className={classes.field}>
          {createNumberFormatFieldWithAdornment({
            store,
            testId: 'postRehabBathroomsCount',
            fieldName: 'postRehabBathroomsCount',
            label: '',
            allowNegative: false,
            thousandSeparator: true,
            decimalScale: 1,
            disabled: disabled,
          })}
        </Box>

        {/* Above Grade */}
        <Typography className={classes.label}>
          Sq ft above grade
        </Typography>

        {showPreRehab && (
          <Box className={classes.field}>
            {createNumberFormatFieldWithAdornment({
              store,
              testId: 'preRehabAproxSquareFootageAboveGrade',
              fieldName: 'preRehabAproxSquareFootageAboveGrade',
              label: '',
              allowNegative: false,
              thousandSeparator: true,
              adornment: 'sq. ft.',
              decimalScale: 0,
              disabled: disabled,
            })}
          </Box>
        )}

        <Box className={classes.field}>
          {store.shouldSyncPostRehabSqFt ? (
            <SameAsPreRehab />
          ) : (
            createNumberFormatFieldWithAdornment({
              store,
              testId: 'postRehabAproxSquareFootageAboveGrade',
              fieldName: 'postRehabAproxSquareFootageAboveGrade',
              label: '',
              allowNegative: false,
              thousandSeparator: true,
              adornment: 'sq. ft.',
              decimalScale: 0,
              disabled: disabled,
            })
          )}
        </Box>

        {/* Below Grade */}
        <Typography className={classes.label}>
          Sq ft below grade
        </Typography>

        {showPreRehab && (
          <Box className={classes.field}>
            {createNumberFormatFieldWithAdornment({
              store,
              testId: 'preRehabAproxSquareFootageBelowGrade',
              fieldName: 'preRehabAproxSquareFootageBelowGrade',
              label: '',
              allowNegative: false,
              thousandSeparator: true,
              adornment: 'sq. ft.',
              decimalScale: 0,
              disabled: disabled,
            })}
          </Box>
        )}

        <Box className={classes.field}>
          {store.shouldSyncPostRehabSqFt ? (
            <SameAsPreRehab />
          ) : (
            createNumberFormatFieldWithAdornment({
              store,
              testId: 'postRehabAproxSquareFootageBelowGrade',
              fieldName: 'postRehabAproxSquareFootageBelowGrade',
              label: '',
              allowNegative: false,
              thousandSeparator: true,
              adornment: 'sq. ft.',
              decimalScale: 0,
              disabled: disabled,
            })
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default RehabDetailsSection;
